import { assertBlocks } from "./blocks";
import { assertInteraction, isInteractive } from "./interactive";
import { assertEffects } from "./effects";
import { assertWarp } from "./deformation";
import { assertText } from "./text";
import { assertVector } from "./vector-path";
import { assertCorners } from "./corners";
import type { EngineDocument, SceneNode } from "../scene/model";
import { assertEngineDocument } from "../scene/validation";
import { polygonParameters } from "./shapes";
import { assertNativeAppearance } from "./appearance";
import { assertNativeLayer } from "./layer-commands";
import { nativeImageSource } from "./image-formats";

export function emptyNativeDocument(): EngineDocument {
  return {
    schemaVersion: 1,
    activePageId: "page-1",
    assets: [],
    extensions: { nativePrototype: 1 },
    pages: [
      {
        id: "page-1",
        name: "Страница 1",
        exportName: "page-1",
        size: { width: 960, height: 640 },
        unit: "px",
        workspaceColor: "#edf0f5",
        backgroundVisible: true,
        nodes: [],
        extensions: {},
      },
    ],
  };
}

export function createNativeNode(
  id: string,
  kind: "rectangle" | "image" | "ellipse" | "polygon",
  x: number,
  y: number,
  width: number,
  height: number,
): SceneNode {
  return {
    id,
    kind,
    transform: { x, y, originX: "center", originY: "center" },
    geometry: {
      width,
      height,
      ...(kind === "polygon"
        ? { sides: 3, star: false, innerRatio: 0.45 }
        : {}),
    },
    appearance: { fill: "#5977ed", opacity: 1 },
    text: {},
    interaction: { role: kind === "image" ? "decorImage" : "decorShape" },
    effects: {},
    layer: {
      name: {
        image: "Изображение",
        rectangle: "Прямоугольник",
        ellipse: "Эллипс",
        polygon: "Многоугольник",
      }[kind],
    },
    media: {},
  };
}

/** A capability gate, not a migration: never silently discard unsupported artwork. */
export function assertNativeDocument(
  value: unknown,
): asserts value is EngineDocument {
  assertEngineDocument(value);
  const fail = (): never => {
    throw new Error(
      "Этот проект использует возможности, которые ещё не перенесены в новый движок. Откройте его в полной версии Creator Engine Pro. Текущий документ сохранён на холсте.",
    );
  };
  const interactive = value.extensions.nativeMode === "interactive";
  const objectIds = new Set<string>(),
    exportNames = new Set<string>(),
    assets = new Map(value.assets.map((a) => [a.id, a]));
  if (
    value.extensions.nativePrototype !== 1 ||
    (!interactive && value.pages.length !== 1)
  )
    fail();
  for (const page of value.pages) {
    assertBlocks(page.nodes);
    if (interactive) {
      if (
        !/^[a-zA-Z0-9_-]+$/.test(page.exportName) ||
        exportNames.has(page.exportName)
      )
        fail();
      exportNames.add(page.exportName);
    }
    if (
      !/^#[0-9a-f]{6}$/i.test(page.workspaceColor) ||
      !["px", "mm", "cm"].includes(page.unit) ||
      ![page.size.width, page.size.height].every((n) => n >= 1 && n <= 16384)
    )
      fail();
    const ids = new Set<string>();
    for (const node of page.nodes) {
      if (ids.has(node.id)) fail();
      ids.add(node.id);
      if (
        !["rectangle", "image", "ellipse", "polygon", "text", "path"].includes(
          node.kind,
        ) ||
        node.children ||
        node.clip ||
        node.compatibility
      )
        fail();
      const t = node.transform,
        g = node.geometry;
      if (
        t.originX !== "center" ||
        t.originY !== "center" ||
        (t.scaleX !== undefined && t.scaleX !== 1) ||
        (t.scaleY !== undefined && t.scaleY !== 1) ||
        (t.rotation !== undefined && !Number.isFinite(t.rotation)) ||
        (t.skewX !== undefined &&
          (!Number.isFinite(t.skewX) ||
            Math.abs(Math.tan((t.skewX * Math.PI) / 180)) > 1e5)) ||
        t.skewY ||
        t.flipX ||
        t.flipY
      )
        fail();
      if (
        ![t.x, t.y, g.width, g.height].every(
          (n) =>
            typeof n === "number" && Number.isFinite(n) && Math.abs(n) <= 1e6,
        ) ||
        g.width! < 1 ||
        g.height! < 1 ||
        Object.keys(g).some(
          (k) =>
            ![
              "width",
              "height",
              "cornerRadius",
              "warpCorners",
              "warpEdges",
              "warpSourceSize",
              "warpMapping",
              ...(node.kind === "path" ? ["vector", "svgPath"] : []),
              ...(node.kind === "polygon"
                ? ["sides", "star", "innerRatio"]
                : []),
            ].includes(k),
        )
      )
        fail();
      if (node.kind === "polygon") polygonParameters(node);
      assertCorners(node);
      assertEffects(node);
      assertWarp(node);
      if (node.kind === "text") assertText(node);
      if (node.kind === "path") assertVector(node);
      assertNativeAppearance(node);
      assertNativeLayer(node);
      if (node.kind !== "text" && Object.keys(node.text).length) fail();
      assertInteraction(node, interactive);
      if (isInteractive(node)) {
        const id = String(node.interaction.objectId);
        if (objectIds.has(id)) fail();
        objectIds.add(id);
        if (
          node.interaction.targetScreenId &&
          !value.pages.some((p) => p.id === node.interaction.targetScreenId)
        )
          fail();
        if (
          node.interaction.interactiveImageEnabled &&
          !node.media.interactiveImage
        )
          fail();
      }
      for (const [slot, id] of Object.entries(node.media)) {
        if (
          assets.get(id!)?.kind !== (slot.endsWith("Sound") ? "audio" : "image")
        )
          fail();
      }
      if (
        Object.keys(node.media).some(
          (k) =>
            ![
              "image",
              ...(isInteractive(node)
                ? [
                    "interactiveImage",
                    "hoverSound",
                    "clickSound",
                    "releaseSound",
                  ]
                : []),
            ].includes(k),
        ) ||
        (node.kind === "image" && !node.media.image)
      )
        fail();
    }
  }
  for (const asset of value.assets) {
    if (
      asset.kind === "audio"
        ? !interactive || !/^data:audio\/[\w.+-]+;base64,/i.test(asset.source)
        : !nativeImageSource(asset.source)
    )
      fail();
  }
}
